import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { Badge, cn } from '@ludus/ui'
import { fetchSales, type SaleApiError, type SaleSummary } from '@/lib/api/ludus-leads.ts'
import { CenteredPanel } from '@/shared/feedback/CenteredPanel.tsx'

/**
 * Every sale in one place (#338): one row per cycle, and inside it every board
 * the sale touches with the cards it holds there and the stage each stands on.
 *
 * Read-only, like the sale page it links to — a card is edited on its own
 * page, a sale is explained on its own page. This one answers the question
 * neither of them can: "where is each order, on every board, right now".
 *
 * It reads `GET /api/ludus-leads`, which answers the whole list as one slice
 * (views + the card, board and stage rows their ids name), so a page of fifty
 * sales is one request rather than fifty.
 */
export function SalesListPage() {
  const { t } = useTranslation()
  const swr = useSWR<SaleSummary[], SaleApiError>('/ludus-leads', () => fetchSales())

  if (swr.isLoading) {
    return (
      <CenteredPanel title={t('pages.sales.loading', { defaultValue: 'Loading sales…' })} muted />
    )
  }
  if (swr.error) {
    return (
      <CenteredPanel
        title={t('pages.sales.error.title', { defaultValue: 'Could not load sales' })}
        body={t('pages.sales.error.generic', {
          defaultValue: 'The engine returned an error while loading the sales.',
        })}
        meta={
          swr.error.status !== null ? `HTTP ${swr.error.status} · ${swr.error.message}` : undefined
        }
        tone="error"
      />
    )
  }
  const sales = swr.data ?? []

  return (
    <div className="flex w-full flex-1 flex-col gap-4 p-4 sm:p-6">
      <header className="flex flex-wrap items-baseline gap-2">
        <h1 className="text-xl font-semibold text-foreground">
          {t('pages.sales.title', { defaultValue: 'Sales' })}
        </h1>
        <span className="text-xs text-muted">
          {t('pages.sales.subtitle', {
            defaultValue: 'Every connected card, on every board, with its stage.',
          })}
        </span>
      </header>

      {sales.length === 0 ? (
        <CenteredPanel
          title={t('pages.sales.empty.title', { defaultValue: 'No sales yet' })}
          body={t('pages.sales.empty.body', {
            defaultValue:
              'A sale appears once the linking pass has placed a card on it — every card that arrives from now on is placed as it arrives.',
          })}
          muted
        />
      ) : (
        <ul className="flex flex-col gap-3">
          {sales.map((sale) => (
            <SaleRow key={sale.id} sale={sale} />
          ))}
        </ul>
      )}
    </div>
  )
}

function SaleRow({ sale }: { sale: SaleSummary }) {
  const { t, i18n } = useTranslation()
  const title = sale.boards[0]?.cards[0]?.name ?? sale.id

  return (
    <li className="flex flex-col rounded-xl border border-border bg-background-secondary">
      <header className="flex flex-wrap items-center gap-2 border-b border-border px-4 py-2">
        <Link
          to={`/sales/${sale.id}`}
          className="min-w-0 truncate text-sm font-semibold text-foreground hover:underline"
        >
          {title}
        </Link>
        <Badge tone={sale.closedAt === null ? 'primary' : 'muted'} size="sm">
          {sale.closedAt === null
            ? t('pages.sales.open', { defaultValue: 'Open' })
            : t('pages.sales.closed', { defaultValue: 'Closed' })}
        </Badge>
        {sale.anomalyFlags.map((flag) => (
          <Badge key={flag} tone="warning" size="sm">
            {t(`pages.sale.anomaly.${flag}`, { defaultValue: flag })}
          </Badge>
        ))}
        <span className="ml-auto shrink-0 text-[10px] text-muted">
          {t('pages.sales.lastCard', { defaultValue: 'Last card' })}{' '}
          {sale.lastCardAt.toLocaleString(i18n.language)}
        </span>
      </header>

      {/* One column per board the sale touches: the card there and its stage.
          The stage is the whole point of the page — a sale is where its cards
          stand, and each board says so in its own vocabulary. */}
      <div className="grid gap-px bg-border/50 sm:grid-cols-2 lg:grid-cols-3">
        {sale.boards.map((board) => (
          <section key={board.id} className="flex flex-col gap-1 bg-background-secondary px-4 py-2">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-wide text-muted">{board.name}</span>
              {board.hasAdapter && (
                <Badge tone="info" size="sm">
                  {t('pages.sale.mirrored', { defaultValue: 'Mirrored' })}
                </Badge>
              )}
            </div>
            <ul className="flex flex-col gap-1">
              {board.cards.map((card) => (
                <li key={card.id}>
                  <Link
                    to={`/pipelines/${card.pipelineId}/leads/${card.id}`}
                    className="flex items-center gap-2 rounded-md px-1 py-0.5 text-sm transition-colors hover:bg-sidebar-hover-bg"
                  >
                    <span className="min-w-0 flex-1 truncate text-foreground">{card.name}</span>
                    <span
                      className={cn(
                        'shrink-0 rounded-full border px-2 py-0.5 text-[11px]',
                        card.lifecyclePhase === 'won'
                          ? 'border-transparent bg-toast-success-bg text-toast-success-text'
                          : card.lifecyclePhase === 'lost'
                            ? 'border-transparent bg-toast-error-bg text-toast-error-text'
                            : 'border-border text-foreground',
                      )}
                    >
                      {card.statusName ?? t('pages.sales.unknownStage', { defaultValue: '—' })}
                    </span>
                    {card.confidence === 'low' && (
                      <Badge tone="warning" size="sm">
                        {t('pages.sale.link.low', { defaultValue: 'Disputed' })}
                      </Badge>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </li>
  )
}
